/** Every write a post takes, for both programmes, as mutations that refresh
 *  every screen showing posts when they succeed.
 *
 *  The two sides keep their own endpoints — a desk post is scheduled with
 *  /redeem/posts/:id/schedule, a campaign draft is (re)slotted by approving it
 *  with a time — and this file is where "move this post to 21:00" becomes the
 *  right call for either. */

import { useMutation, useMutationState, useQueryClient } from '@tanstack/react-query'
import { api, type RedeemPost } from '@/api/client'

export type PostKind = 'redeem' | 'draft'

export interface PostRef {
  kind: PostKind
  id: number
}

/** The query keys a post write can change: the schedule, both sides' lists
 *  and details, the counts on the review badge and the attention strip, and
 *  the desk numbers a publish moves. */
export const POST_KEYS = [
  'schedule',
  'redeemPosts',
  'redeemPost',
  'publicCodes',
  'drafts',
  'draft',
  'draftCounts',
  'deskHealth',
  'deskStats',
  'redeemApps',
  'redeemApp',
] as const

export function useInvalidatePosts() {
  const qc = useQueryClient()
  return () => Promise.all(POST_KEYS.map((k) => qc.invalidateQueries({ queryKey: [k] })))
}

/** A slot for either side. A desk post takes the time as is; a campaign draft
 *  is approved at that time — the same call that moves an approved, failed or
 *  scheduled one. */
export function schedulePost(ref: PostRef, at: string) {
  return ref.kind === 'redeem' ? api.scheduleRedeemPost(ref.id, at) : api.approveDraft(ref.id, at)
}

/** Off the schedule, kept: a desk post goes back to draft, a campaign draft
 *  back to review (approving it again earns a fresh slot). */
export function unschedulePost(ref: PostRef) {
  return ref.kind === 'redeem' ? api.scheduleRedeemPost(ref.id, null) : api.unscheduleDraft(ref.id)
}

/** Waits for the platform; the error is the platform's own words. */
export function publishPostNow(ref: PostRef) {
  return ref.kind === 'redeem' ? api.publishRedeemPostNow(ref.id) : api.publishDraftNow(ref.id)
}

/** Publish-now sends to a real platform, so it is the one write that must not
 *  run twice for the same post. Every screen asks this one question. */
export const PUBLISH_NOW_KEY = ['publishNow'] as const

/** The posts a publish-now is waiting on right now, from any screen. */
export function usePublishingRefs(): PostRef[] {
  return useMutationState({
    filters: { mutationKey: PUBLISH_NOW_KEY, status: 'pending' },
    select: (m) => m.state.variables as PostRef,
  })
}

export function usePostActions() {
  const invalidate = useInvalidatePosts()
  // onSettled, not onSuccess: a write that fails has usually changed the post
  // anyway (a failed publish leaves it `failed` with a new reason), and a
  // screen that waits for the next poll shows the old state for half a minute.
  const done = { onSettled: () => invalidate() }
  return {
    schedule: useMutation({ mutationFn: (v: { ref: PostRef; at: string }) => schedulePost(v.ref, v.at), ...done }),
    unschedule: useMutation({ mutationFn: (ref: PostRef) => unschedulePost(ref), ...done }),
    publishNow: useMutation({ mutationKey: PUBLISH_NOW_KEY, mutationFn: (ref: PostRef) => publishPostNow(ref), ...done }),
    /** A campaign draft out of review; without a time the campaign's pacing
     *  picks the next free slot and the answer says which. */
    approve: useMutation({
      mutationFn: (v: { id: number; at?: string }) =>
        api.approveDraft(v.id, v.at) as Promise<{ ok: boolean; scheduled_at?: string }>,
      ...done,
    }),
    reject: useMutation({ mutationFn: (v: { id: number; reason: string }) => api.rejectDraft(v.id, v.reason), ...done }),
    markDraftManual: useMutation({
      mutationFn: (v: { id: number; url: string }) => api.markPublishedManual(v.id, v.url),
      ...done,
    }),
    editDraft: useMutation({
      mutationFn: (v: { id: number; body: string; mediaIds: number[]; meta: Record<string, unknown> }) =>
        api.editDraft(v.id, v.body, v.mediaIds, v.meta),
      ...done,
    }),
    createDraft: useMutation({ mutationFn: api.createDraft, ...done }),
    updateRedeem: useMutation({
      mutationFn: (v: { id: number; post: Partial<RedeemPost> }) => api.updateRedeemPost(v.id, v.post),
      ...done,
    }),
    createRedeem: useMutation({ mutationFn: (p: Partial<RedeemPost>) => api.createRedeemPost(p), ...done }),
    /** A publish that died between starting and recording leaves the row
     *  "publishing" forever; returning it to a draft is the only way out. */
    revertRedeem: useMutation({
      mutationFn: (post: RedeemPost) =>
        api.updateRedeemPost(post.id, { ...post, status: 'draft', scheduled_at: undefined }),
      ...done,
    }),
    /** A campaign draft the judge or a person turned down, or a send stuck in
     *  publishing: back to the review queue. */
    revertDraft: useMutation({ mutationFn: (id: number) => api.revertDraftToReview(id), ...done }),
    moveDraft: useMutation({
      mutationFn: (v: { id: number; campaignId: number }) => api.moveDraft(v.id, v.campaignId),
      ...done,
    }),
    deleteRedeem: useMutation({ mutationFn: (id: number) => api.deleteRedeemPost(id), ...done }),
    markRedeemPublished: useMutation({
      mutationFn: (v: { id: number; url: string }) => api.markRedeemPostPublished(v.id, v.url),
      ...done,
    }),
    topUpCodes: useMutation({
      mutationFn: (v: { id: number; count: number }) => api.topUpRedeemPublicCodes(v.id, v.count),
      ...done,
    }),
    voidCode: useMutation({ mutationFn: (codeId: number) => api.setRedeemCodeState(codeId, 'void'), ...done }),
  }
}

export type PostActions = ReturnType<typeof usePostActions>
